import { useEffect, useMemo, useState } from 'react';
import { toNumber } from 'lodash';
import { numberUtil, dateUtil } from '@xtnd/web/shared/utils';
import {
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  Drawer,
  DrawerBody,
  Icon,
  Input,
  showToast,
  ClioLinkButton,
  Card,
  Skeleton,
} from '@repo/ui';
import { ArClientType, ArLedgerReportType } from '@repo/types';

import { MatterArTab, useMatterARContext } from '../common';
import { useMatterDetailQuery, useMatterInvoicesQuery } from '../../queries';
import {
  ArEmailQueueMatter,
  ArMatter,
  ArMatterResponse,
  LedgerReportForm as FormValues,
  MatterArTabId,
} from '../../types';
import { useQueryKeyStore } from '@xtnd/web/shared/stores';
import { queryClient } from '@xtnd/web/providers';
import InvoiceList from './InvoiceList';
import AttemptsSection from './AttemptsSection';
import LedgerReportForm from './LedgerReportForm';
import MatterLedgerReportModal from './MatterLedgerReportModal';
import CollectionAttemptForm from './CollectionAttemptForm';
import { AR_COLLECTIONS_LOCATION, DAYS_PAST_DUE } from '../../constants';
import { PreviewEmailModal } from '../email/PreviewEmailModal';
import { populateMatterInvoiceSummary } from '../email/utils';
import { arMatterApi } from '../../api';
import { SIRBadge } from '../SIRBadge';

const InitialLedgerValues: FormValues = {
  runDate: null,
  dateRange: { from: null, to: null },
  ledgerType: ArLedgerReportType.MATTER,
  ledgerData: null,
};
type PaidKey = 'paidByInsured' | 'paidByCarrier';

export default function MatterDetailDrawer() {
  const {
    isOpen,
    currentMatterId,
    currentClientId,
    closeMatterDrawer,
    openInvoiceDetail,
    daysPastDue,
    daysPastDueCap,
  } = useMatterARContext();
  const { data, isLoading, isError } = useMatterDetailQuery(
    currentMatterId,
    currentClientId,
    daysPastDue,
    daysPastDueCap
  );
  const [activeTab, setActiveTab] = useState<MatterArTabId>('invoices');
  const [isOpenLedgeForm, setIsOpenLedgeForm] = useState<boolean>(false);
  const [isGenerateLedgeReport, setIsGenerateLedgerReport] = useState<boolean>(false);
  const [isOpenAttemptForm, setIsOpenAttemptForm] = useState<boolean>(false);
  const [ledgerFormValues, setLedgerFormValues] = useState<FormValues>(InitialLedgerValues);
  const [isPreviewEmail, setIsPreviewEmail] = useState<boolean>(false);
  const [form, setForm] = useState<{
    paidByInsured: string;
    paidByCarrier: string;
    errorAmountByInsured: string;
    errorAmountByCarrier: string;
  }>({
    paidByInsured: '',
    paidByCarrier: '',
    errorAmountByInsured: '',
    errorAmountByCarrier: '',
  });
  const [isLoadingCapture, setIsLoadingCapture] = useState<boolean>(false);

  const canSendEmail = data?.data?.canSendEmail;

  const AMOUNT_REGEX = /^\d*\.?\d{0,2}$/;

  const handlePaidAmount = (e: React.ChangeEvent<HTMLInputElement>, key: PaidKey) => {
    const raw = e.target.value;
    const normalized = numberUtil.normalizeNumericString(raw);
    if (!AMOUNT_REGEX.test(normalized)) return;
    setForm(prev => {
      const value = Number(normalized);

      const errorKey = key === 'paidByInsured' ? 'errorAmountByInsured' : 'errorAmountByCarrier';

      let error = '';

      if (normalized && value < 0) {
        error = 'Amount must be greater than or equal to 0';
      }

      return {
        ...prev,
        [key]: normalized,
        [errorKey]: error,
        errorAmountTotal: '',
      };
    });
  };

  const matter = useMemo(() => data?.data, [data]);
  const { getQueryKey } = useQueryKeyStore();
  const matterKey = getQueryKey('ar-collections', 'queue.matters');
  const formatPaidAmount = (value?: number): string => {
    if (value === 0) return '0';
    if (value == null) return '';
    return value.toString();
  };
  useEffect(() => {
    if (matter) {
      setForm(prev => ({
        ...prev,
        paidByInsured: formatPaidAmount(matter.paidByInsured),
        paidByCarrier: formatPaidAmount(matter.paidByCarrier),
      }));
    }
  }, [matter]);

  const handleCloseLedgerReport = () => {
    setIsOpenLedgeForm(false);
    setLedgerFormValues(InitialLedgerValues);
  };

  const handleGenerateLedgerReport = () => {
    setIsOpenLedgeForm(false);
    setIsGenerateLedgerReport(true);
  };

  const handleCloseLedgerReportModal = () => {
    setIsGenerateLedgerReport(false);
  };

  const handleCloseAddAttemptModal = () => {
    setIsOpenAttemptForm(false);
  };

  const handleViewLedgerReport = () => {
    setIsOpenLedgeForm(true);
    setLedgerFormValues({
      ...InitialLedgerValues,
      ledgerData: currentMatterId,
    });
  };

  const handleCloseEmail = () => {
    setIsPreviewEmail(false);
  };
  const { data: matterInvoice } = useMatterInvoicesQuery(
    currentMatterId,
    currentClientId,
    daysPastDue,
    daysPastDueCap
  );

  const summary = populateMatterInvoiceSummary(
    matterInvoice?.data || [],
    matter?.clientId,
    matter?.matterId
  );

  const handleCaptureArMatterData = async () => {
    if (!matter?.matterClientDeductible) {
      return;
    }
    if (form.errorAmountByCarrier || form.errorAmountByInsured) {
      return;
    }
    setIsLoadingCapture(true);
    try {
      const arMatterPayload: ArMatter = {
        matterId: matter?.matterId,
        matterNumber: matter?.matterNumber,
        matterDisplayNumber: matter?.matterDisplayNumber || '',
        matterDescription: matter?.matterDescription || '',
        clientId: matter?.clientId || '',
        clientType: matter?.clientType as ArClientType,
        clientName: matter?.clientName ?? '',
        matterClientDeductible: toNumber(matter.matterClientDeductible),
        paidByInsured: toNumber(form.paidByInsured),
        paidByCarrier: toNumber(form.paidByCarrier),
        totalOutstandingFees: toNumber(matter?.outstandingFees) ?? 0,
        totalOutstandingExpenses: toNumber(matter?.outstandingExpenses) ?? 0,
        totalOutstanding: toNumber(matter?.totalOutstanding) ?? 0,
        numOfInvoices: toNumber(matter.numOfInvoices),
      };
      await arMatterApi.captureArMatterData(arMatterPayload);

      queryClient.setQueryData(matterKey, (oldData: ArMatterResponse) => {
        if (!oldData?.data) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            paidByInsured: formatPaidAmount(arMatterPayload.paidByInsured),
            paidByCarrier: formatPaidAmount(arMatterPayload.paidByCarrier),
          },
        };
      });

      setIsLoadingCapture(false);
      showToast.success(`Capture data successfully`);
    } catch (error) {
      setIsLoadingCapture(false);
      showToast.error(
        `Capture data failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={closeMatterDrawer}
        title="Matter AR Detail"
        showCloseButton
        height="lg"
        className="cursor-default dark:bg-xtnd-dark bg-white min-h-[95vh]"
      >
        {isLoading && (
          <Card className="p-6 space-y-6 animate-pulse">
            <div className="grid grid-cols-10 gap-x-16 gap-y-6">
              <div className="space-y-4 col-span-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="space-y-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                ))}
              </div>
              <div className="space-y-4 col-span-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="space-y-1">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-5 w-28" />
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-4 col-span-2">
                <Skeleton className="min-h-8 max-w-40 rounded-md" />
                <Skeleton className="min-h-8 max-w-40 rounded-md" />
              </div>
            </div>
            <div className="flex gap-4">
              <Skeleton className="min-h-8 w-64 rounded-md" />
              <Skeleton className="min-h-8 w-64 rounded-md" />
            </div>
            <div className="flex gap-3 flex-wrap">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-40 rounded-md" />
              ))}
              <Skeleton className="h-14 w-14 rounded-md" />
            </div>
          </Card>
        )}

        {!isLoading && isError && (
          <DrawerBody>
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <Icon name="HiOutlineExclamationCircle" className="h-10 w-10 text-red-500" />
              <p className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                Unable to load matter details
              </p>
              <p className="max-w-sm text-sm text-gray-600 dark:text-gray-400">
                This matter's financial details couldn't be retrieved. It may not be synced with
                Clio yet, or Clio may be temporarily unavailable.
              </p>
            </div>
          </DrawerBody>
        )}

        {!isLoading && !isError && !matter && (
          <DrawerBody>
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <Icon name="HiOutlineExclamationCircle" className="h-10 w-10 text-gray-400" />
              <p className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">
                No outstanding invoices found
              </p>
              <p className="max-w-sm text-sm text-gray-600 dark:text-gray-400">
                This matter has no outstanding invoices matching the current filters.
              </p>
            </div>
          </DrawerBody>
        )}

        {!isLoading && !isError && matter && (
          <DrawerBody>
            <div className="flex flex-col gap-6 text-sm font-bold text-xtnd-dark dark:text-xtnd-light after:content-[''] after:block after:w-full after:border-b after:dark:border-xtnd-dark">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4 max-w-4xl">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-semibold">{matter?.matterDisplayNumber}</span>

                      <ClioLinkButton id={matter?.matterId} modal="matters" />
                    </div>

                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {matter?.clientName}
                    </span>
                  </div>
                  {matter?.isSIR && <SIRBadge />}
                </div>
                <div className={`gap-4 flex-col flex items-center`}>
                  <Button variant="outline" onClick={handleViewLedgerReport}>
                    View Ledger Report
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsPreviewEmail(true);
                    }}
                    disabled={!canSendEmail}
                    leftIcon={<Icon name="FiSend" />}
                    className="!bg-xtnd-blue !h-[38px] text-xtnd-white hover:!bg-xtnd-blue-600 !border-xtnd-blue focus:!ring-xtnd-blue-500 !w-[164px]"
                  >
                    Send Email
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-[2fr_1fr] gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <span className="font-medium">Total AR</span>
                    <strong>{numberUtil.formatCurrency(matter?.totalOutstanding || 0)}</strong>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">Oldest Days Past Due</span>
                    <strong
                      className={`inline-block py-1 px-2 w-fit rounded-full text-xtnd-dark dark:text-xtnd-light ${(matter?.maxDaysPastDue || 0) > DAYS_PAST_DUE ? 'bg-[#ef4444] text-xtnd-white' : 'dark:bg-xtnd-dark bg-xtnd-light'}`}
                    >
                      {`${matter?.maxDaysPastDue} days`}
                    </strong>
                  </div>

                  <div className="flex flex-col">
                    <span className="font-medium">Outstanding Fees</span>
                    <strong>{numberUtil.formatCurrency(matter?.outstandingFees || 0)}</strong>
                  </div>

                  <div className="flex flex-col">
                    <span className="font-medium">Last Contact</span>
                    <strong>
                      {matter?.lastContact
                        ? dateUtil.formatDate(matter.lastContact, dateUtil.DateFormat.US)
                        : 'N/A'}
                    </strong>
                  </div>

                  <div className="flex flex-col">
                    <span className="font-medium">Outstanding Expenses</span>
                    <strong>{numberUtil.formatCurrency(matter?.outstandingExpenses || 0)}</strong>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">Next Flow-Up</span>
                    <strong>
                      {matter?.nextFollowUp
                        ? dateUtil.formatDate(matter.nextFollowUp, dateUtil.DateFormat.US)
                        : 'N/A'}
                    </strong>
                  </div>

                  <div className="flex flex-col">
                    <span className="font-medium">Total WIP</span>
                    <strong>{numberUtil.formatCurrency(matter?.totalWipBalance || 0)}</strong>
                  </div>

                  <div className="flex flex-col">
                    <span className="font-medium">Total Trust</span>
                    <strong>{numberUtil.formatCurrency(matter?.totalTrustBalance || 0)}</strong>
                  </div>
                </div>

                {matter?.matterClientDeductible ? (
                  <div className="grid grid-cols-1 gap-4">
                    <span className="font-medium">
                      Client Deductible:{' '}
                      <strong className="inline-block py-1 px-2 w-fit rounded-full text-center text-xtnd-dark dark:text-xtnd-light bg-orange-300">
                        {numberUtil.formatCurrency(matter.matterClientDeductible) || 0}
                      </strong>
                    </span>

                    <Input
                      placeholder="$ Paid by Insured"
                      label="Paid by Insured"
                      type="number"
                      min="-1"
                      value={form.paidByInsured}
                      error={form.errorAmountByInsured}
                      onChange={e => {
                        handlePaidAmount(e, 'paidByInsured');
                      }}
                      onKeyDown={e => {
                        if (['e', 'E', '+', '-'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                    <Input
                      placeholder="$ Paid by Carrier"
                      label="Paid by Carrier"
                      type="number"
                      min="-1"
                      value={form.paidByCarrier}
                      error={form.errorAmountByCarrier}
                      onChange={e => {
                        handlePaidAmount(e, 'paidByCarrier');
                      }}
                      onKeyDown={e => {
                        if (['e', 'E', '+', '-'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />

                    <Button
                      variant="outline"
                      leftIcon={<Icon name="FiSave" />}
                      className="!bg-xtnd-blue !h-[38px] text-xtnd-white hover:!bg-xtnd-blue-600 !border-xtnd-blue focus:!ring-xtnd-blue-500 !w-[202px]"
                      loading={isLoadingCapture}
                      disabled={isLoadingCapture}
                      onClick={handleCaptureArMatterData}
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-4 p-4 text-sm">
              <MatterArTab activeTab={activeTab} onTabChange={setActiveTab} />

              {activeTab === 'invoices' && (
                <>
                  <InvoiceList
                    onOpenInvoiceDetail={invoice =>
                      openInvoiceDetail({
                        ...invoice,
                        clientName: matter?.clientName ?? '',
                        matterDisplayNumber: matter?.matterDisplayNumber ?? '',
                      })
                    }
                  />
                </>
              )}
              {activeTab === 'attempts' && (
                <div className="flex flex-col">
                  <Button
                    variant="primary"
                    className="flex items-center gap-2"
                    onClick={() => setIsOpenAttemptForm(true)}
                  >
                    <Icon name="FaPlus" size="xs" />
                    Log Collection Attempt
                  </Button>
                  <AttemptsSection />
                </div>
              )}
            </div>
          </DrawerBody>
        )}
      </Drawer>

      <Dialog open={isOpenLedgeForm} showCloseButton onClose={handleCloseLedgerReport} size="xxl">
        <DialogHeader>
          <h1>Select Date Range for Ledger Report</h1>
        </DialogHeader>
        <DialogBody>
          <LedgerReportForm
            values={ledgerFormValues}
            onChange={formValues => setLedgerFormValues(formValues)}
            onCancel={handleCloseLedgerReport}
            onGenerate={handleGenerateLedgerReport}
          />
        </DialogBody>
      </Dialog>

      <MatterLedgerReportModal
        matterDisplayNumber={matter?.matterDisplayNumber}
        values={ledgerFormValues}
        isOpen={isGenerateLedgeReport}
        onClose={handleCloseLedgerReportModal}
      />

      <Dialog
        open={isOpenAttemptForm}
        onClose={handleCloseAddAttemptModal}
        size="xxl"
        className="overflow-y-auto"
      >
        <DialogHeader>
          <h1>Log New Collection Attempt</h1>
        </DialogHeader>

        <DialogBody>
          <CollectionAttemptForm
            matterId={currentMatterId}
            clientId={currentClientId}
            onCancel={handleCloseAddAttemptModal}
          />
        </DialogBody>
      </Dialog>
      {isPreviewEmail && summary && canSendEmail && (
        <PreviewEmailModal
          open={isPreviewEmail}
          onClose={handleCloseEmail}
          email={summary as ArEmailQueueMatter}
          groupBy={AR_COLLECTIONS_LOCATION}
        />
      )}
    </>
  );
}
